import { PredefinedFiltersEnum, SnackBarVariants } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import ShipmentDetailComponent from '@app/components/shared/shipment-detail.component';
import TablePaginationComponent from '@app/components/shared/table-pagination.component';
import * as constants from '@app/constants';
import { initialShipmentComponentState, IShipment } from '@app/models';
import * as models from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { CarrierAccountService } from '@app/services/carrier-account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { ShipmentService } from '@app/services/shipment.service';
import {
	Avatar,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	FormControl,
	Grid,
	Hidden,
	IconButton,
	List,
	ListItem,
	ListItemAvatar,
	ListItemSecondaryAction,
	ListItemText,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from '@material-ui/core';
import Axios from 'axios';
import dayjs from 'dayjs';
import * as React from 'react';
import { FormattedDate, FormattedNumber, injectIntl } from 'react-intl';
import CustomButton from '@app/components/shared/custom-button.component';
import { ButtonTypeEnum } from '@app/app.enum';

const intlService: IntlService = Injector.get(IntlService);
const shipmentService: ShipmentService = Injector.get(ShipmentService);
const accountService: AccountService = Injector.get(AccountService);
const sharedService: SharedService = Injector.get(SharedService);
const carrierAccountService: CarrierAccountService = Injector.get(CarrierAccountService);

type FilterType = 'startDate' | 'endDate' | 'appId' | 'carrierAccountId' | 'trackingNumber';

// Timeout interval id when Tracking number filter value changes
class ShipmentComponent extends React.Component<models.IShipmentComponentProps, models.IShipmentComponentState> {
	private carrierAccounts: models.ICarrierAccounts[] = [];

	constructor(props: models.IShipmentComponentProps) {
		super(props);
		this.state = initialShipmentComponentState();
	}

	public async componentDidMount() {
		this.carrierAccounts = await carrierAccountService.GetCarrierAccounts(
			null,
			this.props.axiosCancellationToken,
			true,
			true
		);
	}

	public async componentDidUpdate(
		prevProps: models.IShipmentComponentProps,
		prevState: models.IShipmentComponentState
	) {
		if (prevState.appId !== this.state.appId) {
			this.carrierAccounts = await carrierAccountService.GetCarrierAccounts(
				this.state.appId,
				this.props.axiosCancellationToken,
				!this.state.appId,
				true
			);
			this.setState({
				carrierAccountId: '',
			});
		}

		if (this.shouldFetchData(prevState)) {
			this.loadData();
		}
	}

	public render() {
		let list = null;
		if (this.state.results.length > 0) {
			list = <List>{this.state.results.map((x) => this.renderListItem(x))}</List>;
		} else {
			list = (
				<Typography align='center'>{intlService.GetFormattedMessage('app.shipment.no-data-available')}</Typography>
			);
		}
		return (
			<React.Fragment>
				<Card>
					<CardContent>
						<Grid container={true} spacing={2}>
							<Grid item={true} lg={8} md={12} sm={12} xs={12}>
								<Grid container={true} justifyContent='flex-start' spacing={2}>
									<Grid item={true} lg={4} md={4} sm={12} xs={12}>
										<PredefinedFilters
											startDate={this.state.startDate}
											endDate={this.state.endDate}
											onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
										/>
									</Grid>
									<Grid item={true} lg={4} md={4} sm={12} xs={12}>
										<CustomDatePicker
											label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
											maxDate={this.state.endDate}
											disabled={this.state.disableDatePickerFilter}
											value={this.state.startDate}
											onChange={(date: dayjs.Dayjs) => {
												this.setStateAfterCancellingActiveRequests('startDate', date.toDate());
											}}
										/>
									</Grid>
									<Grid item={true} lg={4} md={4} sm={12} xs={12}>
										<CustomDatePicker
											label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
											minDate={this.state.startDate}
											disabled={this.state.disableDatePickerFilter}
											value={this.state.endDate}
											onChange={(date: dayjs.Dayjs) => {
												this.setStateAfterCancellingActiveRequests('endDate', date.toDate());
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item={true} lg={4} md={12} sm={12} xs={12}>
								<Hidden only={['sm', 'xs', 'md']}>
									<Grid container={true} alignItems='center' style={{ height: '100%' }}>
										<Grid item={true} lg={7} md={7} sm={12} xs={12}>
											{/* Filter Button */}
											<Tooltip title={intlService.GetFormattedMessage('app.balance-history.more-filters')}>
												<IconButton
													onClick={() => {
														this.setState({
															filterPanelExpanded: !this.state.filterPanelExpanded,
														});
													}}
												>
													<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
												</IconButton>
											</Tooltip>

											{/* Export Button */}
											<Tooltip title={intlService.GetFormattedMessage('app.common.export')}>
												<IconButton
													onClick={() => {
														this.exportShipments();
													}}
												>
													<Icon name='tray-arrow-down' />
												</IconButton>
											</Tooltip>
											{/* Search Button */}
											<CustomButton
												fullWidth={false}
												type={ButtonTypeEnum.Button}
												buttonText={intlService.GetFormattedMessage('app.common.search')}
												onClick={() => {
													if(this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3)
													{
														return;
													}
													this.loadData();
												}}
											/>
											
										</Grid>	
									</Grid>
								</Hidden>
								<Hidden only={['xl', 'lg']}>
									<Grid container={true} alignItems='center' spacing={3} style={{ height: '100%' }}>
										{/* Filter Button */}
										<Grid item={true}>
											<Button
												variant='outlined'
												color='primary'
												onClick={() => {
													this.setState({
														filterPanelExpanded: !this.state.filterPanelExpanded,
													});
												}}
											>
												<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
												<span className='pl-1'>
													{intlService.GetFormattedMessage('app.balance-history.more-filters')}
												</span>
											</Button>
										</Grid>

										{/* Export Button */}
										<Grid item={true}>
											<Button
												variant='outlined'
												color='primary'
												onClick={() => {
													this.exportShipments();
												}}
											>
												<Icon name='tray-arrow-down' />
												<span className='pl-1'>{intlService.GetFormattedMessage('app.common.export')}</span>
											</Button>
										</Grid>

										{/* Search Button */}
										<Grid item={true}>
											<CustomButton
												fullWidth={false}
												type={ButtonTypeEnum.Button}
												buttonText={intlService.GetFormattedMessage('app.common.search')}
												onClick={() => {
													if(this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3)
													{
														return;
													}
													this.loadData();
												}}
											/>
										</Grid>
									</Grid>
								</Hidden>
							</Grid>
							<Grid item={true} lg={8} md={12} sm={12} xs={12}>
								<Collapse in={this.state.filterPanelExpanded}>
									<Grid container={true} spacing={2}>
										<Grid item={true} lg={4} md={4} sm={12} xs={12}>
											<FormControl fullWidth={true}>
												<TextField
													select={true}
													variant='outlined'
													label={intlService.GetFormattedMessage('app.shipment.app-label')}
													fullWidth={true}
													InputLabelProps={{ shrink: true }}
													SelectProps={{
														displayEmpty: true,
														renderValue: (selected: string) => {
															const selectedApp = accountService
																.getUserData()
																.apps.find((app) => !!selected && app.appId === selected);
															if (selectedApp) {
																return selectedApp.appName;
															}
															return intlService.GetFormattedMessage('app.shipment.all-apps');
														},
													}}
													value={this.state.appId}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														this.setStateAfterCancellingActiveRequests('appId', e.target.value);
													}}
												>
													<MenuItem value={''}>{intlService.GetFormattedMessage('app.shipment.all-apps')}</MenuItem>
													{accountService.getUserData().apps.map((e: models.IAccountAppResponseVM) => (
														<MenuItem key={e.appId} value={e.appId}>
															{e.appName}
														</MenuItem>
													))}
												</TextField>
											</FormControl>
										</Grid>
										<Grid item={true} lg={4} md={4} sm={12} xs={12}>
											<FormControl fullWidth={true}>
												<TextField
													label={intlService.GetFormattedMessage('app.carrier.carrier-account-name-label')}
													InputLabelProps={{ shrink: true }}
													select={true}
													value={this.state.carrierAccountId}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														this.setStateAfterCancellingActiveRequests('carrierAccountId', e.target.value);
													}}
													SelectProps={{
														displayEmpty: true,
														renderValue: (selected: string[]) =>
															selected
																? this.carrierAccounts.find((x) => x.id === selected.toString())?.name
																: intlService.GetFormattedMessage('app.shipment.all-carrierAccounts'),
													}}
													variant='outlined'
												>
													<MenuItem value={''}>
														{intlService.GetFormattedMessage('app.shipment.all-carrierAccounts')}
													</MenuItem>
													{this.carrierAccounts.map((x) => (
														<MenuItem key={x.id} value={x.id}>
															{x.name}
														</MenuItem>
													))}
												</TextField>
											</FormControl>
										</Grid>
										<Grid item={true} lg={4} md={4} sm={12} xs={12}>
											<TextField
												label={intlService.GetFormattedMessage('app.shipment.tracking-number')}
											    placeholder={intlService.GetFormattedMessage('app.shipment.min-tracking-number-placeholder')}
												fullWidth={true}
												value={this.state.trackingNumber}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													this.setStateAfterCancellingActiveRequests('trackingNumber', e.target.value);
												}}
												variant='outlined'
											    error={this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3}
												helperText={this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3 ? 
													intlService.GetFormattedMessage('app.shipment.min-tracking-number-helper-text') : null}
											/>
										</Grid>
									</Grid>
								</Collapse>
							</Grid>

							<Grid container={true}>
								{this.state.dataLoading ? (
									<div className='spinner'>
										<CircularProgress size={32} />
									</div>
								) : (
									<Grid item={true} lg={12} md={12} sm={12} xs={12}>
										{list}
									</Grid>
								)}
								<Grid item={true} lg={12} md={12} sm={12} xs={12}>
									<div className='ml-2'>
										<TablePaginationComponent
											rowsPerPage={this.state.pageSize}
											totalPages={this.state.totalPages}
											count={this.state.totalResults}
											page={this.state.currentPage}
											onChangePage={(event: React.MouseEvent<HTMLInputElement, MouseEvent>, page: number) => {
												this.setState({
													currentPage: page,
												});
											}}
										/>
									</div>
								</Grid>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</React.Fragment>
		);
	}

	private renderListItem(item: IShipment) {
		return (
			<React.Fragment key={item.id}>
				<ListItem
					button={true}
					onClick={() => this.getShipmentDetails(item.id)}
					selected={this.state.collapse[item.id]}
				>
					<ListItemAvatar>
						<Avatar
							classes={{
								root: 'date-avatar',
							}}
						>
							<Grid container={true} justifyContent='center'>
								<Grid item={true}>
									<Typography align='center'>
										<FormattedDate value={sharedService.getNewDateIfNotExist(item.createdDate)} month='short' />
									</Typography>
								</Grid>
								<Grid item={true}>
									<Typography align='center'>
										<FormattedDate value={sharedService.getNewDateIfNotExist(item.createdDate)} day='2-digit' />
									</Typography>
								</Grid>
							</Grid>
						</Avatar>
					</ListItemAvatar>
					<Grid container={true} direction='row' justifyContent='center' alignItems='center' spacing={2}>
						<Grid item={true} xs={9} sm={9} md={9} lg={9} xl={9}>
							<ListItemText
								primary={this.getAddressLine(item.toAddress)}
								secondary={
									<React.Fragment>
										<Typography component='span' variant='body2' color='textPrimary' className='break-word'>
											{intlService.GetFormattedMessage('app.carrier-account-name-label')}:{' '}
											{item.carrierAccountName || '--'}
											<br />
											{item.tracking?.status && intlService.GetFormattedMessage('app.shipment.status')}:{' '}
											{item.tracking?.status} | {intlService.GetFormattedMessage('app.shipment.tracking-number')} :{' '}
											{item.tracking?.trackingNumber}
											{item.packages.length > 1 &&
												this.props.intl.formatHTMLMessage(
													{
														id: 'app.shipment.more-than-one-package-label',
													},
													{ 0: item.packages.length - 1 }
												)}
										</Typography>
									</React.Fragment>
								}
							/>
						</Grid>
						<Grid item={true} xs={3} sm={3} md={3} lg={3} xl={3}>
							<ListItemSecondaryAction>
								<Hidden only='xs'>
									{item.rates ? (
										<Typography variant='h6'>
											{item.refundStatus && (
												<Chip label={item.refundStatus} color='default' size='small' className='mt-0_5 mr-1 mb-1' />
											)}
											$
											<FormattedNumber
												value={item.rates[0].totalFee}
												maximumFractionDigits={2}
												minimumFractionDigits={2}
											/>
										</Typography>
									) : (
										<Typography variant='h6'>
											{item.refundStatus && (
												<Chip label={item.refundStatus} color='default' size='small' className='mt-0_5 mr-1 mb-1' />
											)}
											{intlService.GetFormattedMessage('app.shipment.rate-column-norated')}
										</Typography>
									)}
								</Hidden>
								<Hidden only={['sm', 'md', 'lg', 'xl']}>
									{item.rates ? (
										<Typography variant='subtitle2'>
											$
											<FormattedNumber
												value={item.rates[0].totalFee}
												maximumFractionDigits={2}
												minimumFractionDigits={2}
											/>
										</Typography>
									) : (
										<Typography variant='h6'>
											{intlService.GetFormattedMessage('app.shipment.rate-column-norated')}
										</Typography>
									)}
								</Hidden>
							</ListItemSecondaryAction>
						</Grid>
					</Grid>
				</ListItem>
				<Collapse in={this.state.collapse[item.id]} timeout='auto' unmountOnExit={true}>
					<ShipmentDetailComponent
						shipment={item}
						onShipmentUpdate={(s: models.IShipment) => this.onShipmentUpdate(s)}
					/>
				</Collapse>
				<Hidden only={['xs', 'sm']}>
					<Divider variant='inset' />
				</Hidden>
				<Hidden only={['md', 'lg', 'xl']}>
					<Divider variant='fullWidth' />
				</Hidden>
			</React.Fragment>
		);
	}

	// get address details
	private getAddressLine = (toAddress: models.IAddress) => {
		return (
			intlService.GetFormattedMessage('app.shipment.shipment-list-item-text') +
			[toAddress.street1, toAddress.street2, toAddress.street3, toAddress.city, toAddress.state]
				.filter((x) => x)
				.join(', ')
		);
	};

	private onShipmentUpdate(shipment: models.IShipment) {
		const shipments = this.state.results;
		const shipmentIndex = shipments.findIndex((x) => x.id === shipment.id);
		if (shipmentIndex !== -1) {
			shipments.splice(shipmentIndex, 1, shipment);
			this.setState({
				results: shipments,
			});
		}
	}

	// load shipments Data
	private loadData = async () => {
		try {
			let pageToken = sharedService.generatePaginationToken(this.state.currentPage - 2);

			if (this.state.currentPage === 0) {
				pageToken = undefined;
			}
			this.setState({
				dataLoading: true
			});
			const shipments = await shipmentService.getShipments(
				pageToken,
				this.state.startDate,
				this.state.endDate,
				this.state.appId,
				this.state.carrierAccountId,
				this.state.trackingNumber,
				this.props.axiosCancellationToken
			);

			this.setState({
				...shipments,
				collapse: {},
				dataLoading: false,
			});
		} catch (err) {
			if (!Axios.isCancel(err)) {
				this.props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
			}
		}
	};

	// get shipments details
	private getShipmentDetails = (id: string) => {
		this.setState({
			collapse: {
				...this.state.collapse,
				[id]: !this.state.collapse[id],
			},
		});
	};

	private loadDataForPredefinedFilters = (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			startDate,
			endDate,
			currentPage: 1,
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
		});
	};

	private async exportShipments() {
		try {
			const res = await shipmentService.exportShipments(
				this.state.startDate,
				this.state.endDate,
				this.state.appId,
				this.state.carrierAccountId,
				this.state.trackingNumber,
				this.props.axiosCancellationToken
			);
			const fileName = sharedService.parseContentDisposition(
				res.request.getResponseHeader(constants.contentDispositionHeader)
			);
			sharedService.downloadFile(res.data, fileName);
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private shouldFetchData = (prevState: models.IShipmentComponentState): boolean => {
		return (prevState.currentPage !== this.state.currentPage && !this.props.isRequestCancelled);
	};

	private setStateAfterCancellingActiveRequests = (key: FilterType, value: any) => {
	   const composedFunc = this.callAfterCancellingActiveRequests(() => {
			this.setState((prevState: models.IShipmentComponentState) => ({
				...prevState,
				currentPage: 1,
				[key]: value,
			}));
		 });

		composedFunc();
	};

	private callAfterCancellingActiveRequests = (callback: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
		    callback(...args);
		};
	};
}
export default withAxiosCancellation(withSnackBar(injectIntl(ShipmentComponent)));



// WEBPACK FOOTER //
// ./src/components/shipment/shipment/shipment.component.tsx